import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminModerationService } from 'api/services/AdminModerationService';
import type { ContentReportListItemDto } from 'api/types/moderation/ContentReportListItemDto';
import type { BannedUserListItemDto } from 'api/types/moderation/BannedUserListItemDto';
import type { ModerationUserSearchItemDto } from 'api/types/moderation/ModerationUserSearchItemDto';
import { ReportContentType } from 'api/types/moderation';

const USER_SEARCH_DEBOUNCE_MS = 300;

function contentTypeLabel(t: ReportContentType): string {
  switch (t) {
    case ReportContentType.Profile:
      return 'Profil';
    case ReportContentType.Listing:
      return 'İlan';
    case ReportContentType.Reel:
      return 'Reel';
    default:
      return String(t);
  }
}

const ModerationDashboardPage: React.FC = () => {
  const [tab, setTab] = useState<'reports' | 'bans'>('reports');
  const [reports, setReports] = useState<ContentReportListItemDto[]>([]);
  const [bans, setBans] = useState<BannedUserListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<ModerationUserSearchItemDto[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedBanUser, setSelectedBanUser] = useState<ModerationUserSearchItemDto | null>(null);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const userSearchSeq = useRef(0);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, b] = await Promise.all([
        adminModerationService.listReports(),
        adminModerationService.listBans(),
      ]);
      if (r.isSuccess && r.data) setReports(r.data);
      else setReports([]);
      if (b.isSuccess && b.data) setBans(b.data);
      else setBans([]);
      const errMsg = [!r.isSuccess ? r.message : null, !b.isSuccess ? b.message : null].filter(Boolean).join(' ');
      if (errMsg) setError(errMsg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Yükleme hatası.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (selectedBanUser) {
      setUserSearchResults([]);
      setUserSearchLoading(false);
      return;
    }
    const q = userSearchQuery.trim();
    if (q.length < 2) {
      userSearchSeq.current += 1;
      setUserSearchResults([]);
      setUserSearchLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      const mySeq = ++userSearchSeq.current;
      setUserSearchLoading(true);
      void adminModerationService
        .searchUsers(q)
        .then((res) => {
          if (mySeq !== userSearchSeq.current) return;
          if (res.isSuccess && res.data) setUserSearchResults(res.data);
          else setUserSearchResults([]);
          setUserSearchLoading(false);
        })
        .catch(() => {
          if (mySeq !== userSearchSeq.current) return;
          setUserSearchResults([]);
          setUserSearchLoading(false);
        });
    }, USER_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [userSearchQuery, selectedBanUser]);

  const handleCreateBan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBanUser?.id || !banReason.trim() || banSubmitting) return;
    setBanSubmitting(true);
    try {
      const res = await adminModerationService.createBan({
        userId: selectedBanUser.id,
        reason: banReason.trim(),
      });
      if (res.isSuccess) {
        userSearchSeq.current += 1;
        setSelectedBanUser(null);
        setUserSearchQuery('');
        setUserSearchResults([]);
        setBanReason('');
        await loadAll();
      } else {
        window.alert(res.message ?? 'Ban oluşturulamadı.');
      }
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleRevoke = async (banRowId: string) => {
    if (!window.confirm('Bu banı kaldırmak istediğinize emin misiniz?')) return;
    setRevokingId(banRowId);
    try {
      const res = await adminModerationService.revokeBan(banRowId);
      if (res.isSuccess) await loadAll();
      else window.alert(res.message ?? 'İşlem başarısız.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Moderasyon</h1>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void loadAll()} disabled={loading}>
          Yenile
        </button>
      </div>

      {error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${tab === 'reports' ? 'active' : ''}`}
            onClick={() => setTab('reports')}
          >
            Şikayetler
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${tab === 'bans' ? 'active' : ''}`}
            onClick={() => setTab('bans')}
          >
            Aktif banlar
          </button>
        </li>
      </ul>

      {loading && (
        <div className="py-4 text-center text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Yükleniyor…
        </div>
      )}

      {!loading && tab === 'reports' && (
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Şikayet eden</th>
                <th>Şikayet edilen</th>
                <th>Ticari unvan</th>
                <th>Tür</th>
                <th>İçerik</th>
                <th>Mesaj</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted text-center py-4">
                    Kayıt yok.
                  </td>
                </tr>
              ) : (
                reports.map((row) => (
                  <tr key={row.id}>
                    <td className="text-nowrap small">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '—'}
                    </td>
                    <td className="small">{row.reporterDisplayName ?? '—'}</td>
                    <td className="small">{row.reportedDisplayName ?? '—'}</td>
                    <td className="small">{row.reportedCorporateTradeTitle ?? '—'}</td>
                    <td>{contentTypeLabel(row.contentType as ReportContentType)}</td>
                    <td className="small">{row.contentDisplayLabel ?? '—'}</td>
                    <td className="small" style={{ maxWidth: 280 }}>
                      {row.message ? (
                        <span title={row.message}>{row.message.length > 120 ? `${row.message.slice(0, 120)}…` : row.message}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === 'bans' && (
        <>
          <div className="card mb-4">
            <div className="card-header">Yeni ban (kullanıcıyı platformdan uzaklaştır)</div>
            <div className="card-body">
              <form className="row g-2 align-items-end" onSubmit={handleCreateBan}>
                <div className="col-md-5">
                  <label className="form-label small mb-0" htmlFor="mod-ban-user-search">
                    Kullanıcı
                  </label>
                  <div className="position-relative">
                    {selectedBanUser && (
                      <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                        <span className="badge bg-secondary text-wrap text-start">{selectedBanUser.displayName}</span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => {
                            userSearchSeq.current += 1;
                            setSelectedBanUser(null);
                            setUserSearchQuery('');
                            setUserSearchResults([]);
                          }}
                        >
                          Değiştir
                        </button>
                      </div>
                    )}
                    <input
                      id="mod-ban-user-search"
                      className="form-control form-control-sm"
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setUserPickerOpen(true);
                      }}
                      onFocus={() => setUserPickerOpen(true)}
                      onBlur={() => {
                        window.setTimeout(() => setUserPickerOpen(false), 200);
                      }}
                      placeholder="İsim veya e-posta yazın (en az 2 karakter)"
                      autoComplete="off"
                      disabled={!!selectedBanUser}
                      aria-autocomplete="list"
                      aria-expanded={userPickerOpen && !selectedBanUser}
                    />
                    {userPickerOpen && !selectedBanUser && userSearchQuery.trim().length >= 2 && (
                      <div
                        className="border rounded shadow-sm position-absolute w-100 mt-1"
                        style={{ zIndex: 20, maxHeight: 240, overflowY: 'auto', backgroundColor: 'var(--sb-bg)' }}
                        onMouseDown={(e) => e.preventDefault()}
                        role="listbox"
                      >
                        {userSearchLoading && (
                          <div className="px-3 py-2 text-muted small">
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Aranıyor…
                          </div>
                        )}
                        {!userSearchLoading && userSearchResults.length === 0 && (
                          <div className="px-3 py-2 text-muted small">Sonuç yok.</div>
                        )}
                        {!userSearchLoading &&
                          userSearchResults.map((u) => (
                            <button
                              key={`${u.accountKind}-${u.id}`}
                              type="button"
                              role="option"
                              className="d-block w-100 text-start btn btn-light btn-sm rounded-0 border-0 border-bottom small py-2"
                              onClick={() => {
                                setSelectedBanUser(u);
                                setUserSearchQuery('');
                                setUserSearchResults([]);
                                setUserPickerOpen(false);
                              }}
                            >
                              <span className="d-block fw-medium">{u.displayName}</span>
                              <span className="text-muted">
                                {u.accountKind === 'corporate' ? 'Kurumsal' : 'Bireysel'} · {u.email}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-5">
                  <label className="form-label small mb-0" htmlFor="mod-ban-reason">
                    Gerekçe
                  </label>
                  <input
                    id="mod-ban-reason"
                    className="form-control form-control-sm"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="submit"
                    className="btn btn-danger btn-sm w-100"
                    disabled={banSubmitting || !selectedBanUser}
                  >
                    {banSubmitting ? '…' : 'Ban uygula'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Banlanan kullanıcı</th>
                  <th>Admin</th>
                  <th>Gerekçe</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted text-center py-4">
                      Aktif ban yok.
                    </td>
                  </tr>
                ) : (
                  bans.map((row) => (
                    <tr key={row.id}>
                      <td className="text-nowrap small">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '—'}
                      </td>
                      <td className="small">{row.bannedUserDisplayName ?? '—'}</td>
                      <td className="small">{row.adminDisplayName ?? '—'}</td>
                      <td className="small">{row.reason}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => void handleRevoke(row.id)}
                          disabled={revokingId === row.id}
                        >
                          {revokingId === row.id ? '…' : 'Ban kaldır'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default ModerationDashboardPage;
